import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import { FollowButton } from '../components/FollowButton';
import { MessageButton } from '../components/MessageButton';
import BookingModal from './BookingModal';
import ServiceCard from './ServiceCard';
import PortfolioGrid from './PortfolioGrid';
import { API } from '../config/api';
import {
  Palette, MapPin, Star, Share2, Verified,
  PenTool, Layers, FileText, Image, Frame,
  DollarSign, Loader2, Briefcase, Wrench, Brush
} from 'lucide-react';

const DESIGN_CATEGORIES = {
  'cover_art': { label: 'Cover Art', icon: Frame },
  'flyer_design': { label: 'Flyer Design', icon: Layers },
  'logo_design': { label: 'Logo Design', icon: PenTool },
  'branding': { label: 'Branding', icon: Brush },
  'social_media': { label: 'Social Media Graphics', icon: Image },
};

export default function DesignerProfileView({ profile, roleProfile, isOwnProfile, followerCount, followingCount, isFollowing, onFollowChange }) {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [posts, setPosts] = useState([]);
  const [activeTab, setActiveTab] = useState('portfolio');
  const [loadingContent, setLoadingContent] = useState(true);
  const [showBooking, setShowBooking] = useState(false);

  const rp = roleProfile || {};
  const displayName = rp.display_name || rp.stage_name || profile.name;
  const bio = rp.bio || profile.bio;
  const categories = rp.design_categories || rp.services || rp.specialties || [];
  const priceRange = rp.price_range || profile.rate_range;

  useEffect(() => { fetchContent(); }, [profile.user_id]);

  const fetchContent = async () => {
    setLoadingContent(true);
    try {
      const [portRes, svcRes, postRes] = await Promise.all([
        fetch(`${API}/portfolio?user_id=${profile.user_id}&limit=30`),
        fetch(`${API}/services/user/${profile.user_id}`),
        fetch(`${API}/posts?user_id=${profile.user_id}&limit=20`),
      ]);
      if (portRes.ok) { const d = await portRes.json(); setPortfolio(d.items || []); }
      if (svcRes.ok) { const d = await svcRes.json(); setServices(d.services || []); }
      if (postRes.ok) { const d = await postRes.json(); setPosts(Array.isArray(d) ? d : d.posts || []); }
    } catch (e) { console.error(e); }
    setLoadingContent(false);
  };

  const handleShare = async () => {
    try { await navigator.clipboard.writeText(`${window.location.origin}/u/${profile.handle}`); toast.success('Profile link copied!'); }
    catch { toast.error('Could not copy'); }
  };

  return (
    <div className="min-h-screen" data-testid="designer-profile">
      {/* Banner */}
      <div className="relative h-48 md:h-64 overflow-hidden">
        {rp.banner_url || profile.banner_url ? (
          <img src={rp.banner_url || profile.banner_url} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-pink-900/40 via-zinc-900 to-fuchsia-800/30" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
      </div>

      <div className="max-w-5xl mx-auto px-4 -mt-20 relative z-10">
        <div className="flex flex-col sm:flex-row items-start gap-5">
          {/* Avatar */}
          <div className="w-28 h-28 rounded-2xl border-4 border-background overflow-hidden bg-muted flex-shrink-0 shadow-xl">
            {(rp.avatar_url || profile.avatar_url || profile.picture) ? (
              <img src={rp.avatar_url || profile.avatar_url || profile.picture} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center"><Palette className="w-10 h-10 text-pink-500/50" /></div>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-heading text-2xl md:text-3xl font-bold">{displayName}</h1>
              {(rp.is_verified || profile.is_verified) && <Verified className="w-5 h-5 text-pink-400" />}
              <Badge className="bg-pink-500/20 text-pink-400 border-pink-500/30">Graphic Designer</Badge>
            </div>
            <p className="text-muted-foreground text-sm">{profile.handle ? `@${profile.handle}` : ""}</p>

            {/* Design Categories */}
            {categories.length > 0 && (
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                {categories.map(c => {
                  const key = c.toLowerCase().replace(/\s+/g, '_');
                  const info = DESIGN_CATEGORIES[key] || { label: c, icon: Palette };
                  return (
                    <Badge key={c} variant="outline" className="bg-pink-500/10 border-pink-500/20 text-pink-300 text-xs">
                      <info.icon className="w-3 h-3 mr-1" />{info.label}
                    </Badge>
                  );
                })}
              </div>
            )}

            {/* Location + Rate */}
            <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground flex-wrap">
              {(rp.neighborhood || profile.neighborhood || profile.city || profile.location) && (
                <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{rp.neighborhood || profile.neighborhood || profile.city || profile.location}</span>
              )}
              {priceRange && <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" />{priceRange}</span>}
            </div>

            {/* Stats */}
            <div className="flex items-center gap-5 mt-3">
              <div className="text-center"><p className="font-bold text-lg">{followerCount}</p><p className="text-xs text-muted-foreground">Followers</p></div>
              <div className="text-center"><p className="font-bold text-lg">{followingCount}</p><p className="text-xs text-muted-foreground">Following</p></div>
              <div className="text-center"><p className="font-bold text-lg">{rp.credits_count || 0}</p><p className="text-xs text-muted-foreground">Projects</p></div>
              <div className="text-center"><p className="font-bold text-lg">{portfolio.length}</p><p className="text-xs text-muted-foreground">Works</p></div>
            </div>

            {/* Action Buttons */}
            {!isOwnProfile && (
              <div className="flex items-center gap-2 mt-4 flex-wrap">
                <FollowButton targetUserId={profile.user_id} initialIsFollowing={isFollowing} onFollowChange={onFollowChange} />
                <MessageButton targetUserId={profile.user_id} targetName={displayName} variant="outline" size="default" showText />
                <Button onClick={() => setShowBooking(true)} className="bg-pink-600 hover:bg-pink-700 text-white" data-testid="hire-designer-btn">
                  <PenTool className="w-4 h-4 mr-2" />Hire Designer
                </Button>
                <Button variant="ghost" size="icon" onClick={handleShare} data-testid="share-designer-btn"><Share2 className="w-4 h-4" /></Button>
              </div>
            )}
            {isOwnProfile && (
              <div className="flex items-center gap-2 mt-4">
                <Button variant="outline" onClick={() => navigate('/dashboard/graphic-designer')}>
                  <Palette className="w-4 h-4 mr-2" />Manage Profile
                </Button>
                <Button variant="ghost" size="icon" onClick={handleShare}><Share2 className="w-4 h-4" /></Button>
              </div>
            )}
          </div>
        </div>

        {bio && <p className="mt-5 text-sm text-muted-foreground max-w-2xl leading-relaxed">{bio}</p>}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-8">
          <TabsList className="w-full justify-start bg-muted/30 p-1 mb-6 overflow-x-auto">
            <TabsTrigger value="portfolio" className="flex items-center gap-2" data-testid="gd-tab-portfolio">
              <Layers className="w-4 h-4" />Portfolio{portfolio.length > 0 && <Badge variant="secondary" className="ml-1 text-xs">{portfolio.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="services" className="flex items-center gap-2" data-testid="gd-tab-services">
              <Wrench className="w-4 h-4" />Services{services.length > 0 && <Badge variant="secondary" className="ml-1 text-xs">{services.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="posts" className="flex items-center gap-2" data-testid="gd-tab-posts">
              <FileText className="w-4 h-4" />Posts{posts.length > 0 && <Badge variant="secondary" className="ml-1 text-xs">{posts.length}</Badge>}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="portfolio">
            {loadingContent ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : (
              <PortfolioGrid items={portfolio} emptyIcon={Palette} emptyLabel="No design work yet" isOwnProfile={isOwnProfile} dashboardPath="/dashboard/graphic-designer" />
            )}
          </TabsContent>

          <TabsContent value="services">
            {services.length === 0 ? (
              <Card className="glass-card border-border p-12 text-center">
                <Briefcase className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-medium text-lg mb-2">No services listed</h3>
                <p className="text-muted-foreground">{isOwnProfile ? 'Add services from your dashboard.' : 'No services listed yet.'}</p>
                {isOwnProfile && <Button className="mt-4" onClick={() => navigate('/dashboard/graphic-designer')}>Add Services</Button>}
              </Card>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2">{services.map(s => <ServiceCard key={s.service_id} service={s} onBook={!isOwnProfile ? () => setShowBooking(true) : null} />)}</div>
            )}
          </TabsContent>

          <TabsContent value="posts">
            {posts.length === 0 ? (
              <Card className="glass-card border-border p-12 text-center">
                <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-medium text-lg mb-2">No posts yet</h3>
              </Card>
            ) : (
              <div className="space-y-4">
                {posts.map(p => (
                  <Card key={p.post_id} className="glass-card border-border p-4 cursor-pointer hover:bg-muted/20 transition-colors" onClick={() => navigate(`/post/${p.post_id}`)}>
                    <p className="text-sm">{p.text || p.content}</p>
                    {p.media_url && <img src={p.media_url} alt="" className="mt-3 rounded-lg max-h-64 object-cover" />}
                    <p className="text-xs text-muted-foreground mt-2">{new Date(p.created_at).toLocaleDateString()}</p>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {showBooking && (
        <BookingModal targetId={profile.user_id} targetName={displayName} targetRole="designer" services={services} onClose={() => setShowBooking(false)} />
      )}
    </div>
  );
}
